<script>
  import { onMount } from "svelte";
  import { fade, fly } from "svelte/transition";
  import { cubicOut } from "svelte/easing";
  import { configStore } from "../api/index.js";
  import { Button, Icon } from "./ui/index.js";
  import SupportModal from "./SupportModal.svelte";
  import NoticeBanners from "./NoticeBanners.svelte";
  import NoticeModals from "./NoticeModals.svelte";

  /** @type {{ onGetStarted?: () => void }} */
  let { onGetStarted = () => {} } = $props();

  // Splash screen state
  let showSplash = $state(true);
  let splashFading = $state(false);

  // Support modal state
  let showSupportModal = $state(false);

  // Subscribe to config store
  let config = $state({
    loaded: false,
    loading: false,
    error: null,
    branding: null,
    features: null,
    notices: [],
  });

  // Load config on mount
  onMount(() => {
    const unsubscribe = configStore.subscribe((value) => {
      config = value;
    });

    // Load config if not already loaded
    if (!config.loaded && !config.loading) {
      configStore.load().catch((err) => {
        console.error("Failed to load config:", err);
      });
    }

    // Dismiss splash after a brief delay
    const splashTimer = setTimeout(() => {
      splashFading = true;
      setTimeout(() => {
        showSplash = false;
      }, 600); // Match the fade-out duration
    }, 1500); // Show splash for 1.5 seconds

    return () => {
      unsubscribe();
      clearTimeout(splashTimer);
    };
  });

  // Derived values with fallbacks
  let companyName = $derived(
    config.branding?.companyName || config.branding?.appName || "Provider Drop"
  );
  let appDescription = $derived(
    config.branding?.tagline || "Secure File Upload Platform"
  );
  let logoUrl = $derived(config.branding?.logoUrl || null);
  let phone = $derived(config.branding?.phone || null);
  let email = $derived(config.branding?.email || null);
  let address = $derived(config.branding?.address || null);
  let supportUrl = $derived(config.branding?.supportUrl || null);

  // Logo display options
  let logoMaxWidth = $derived(config.branding?.logoMaxWidth || 400);
  let logoMaxHeight = $derived(config.branding?.logoMaxHeight || 100);
  let logoIsTransparent = $derived(config.branding?.logoIsTransparent || false);
  let logoBackground = $derived(config.branding?.logoBackground || "none");
  let logoContainerStyle = $derived(
    config.branding?.logoContainerStyle || "none"
  );
  let hideCompanyName = $derived(config.branding?.hideCompanyName || false);

  // Notices
  let notices = $derived(config.notices || []);

  // Check if we have any contact info to show
  let hasContactInfo = $derived(phone || email || address);
</script>

<!-- Splash Screen -->
{#if showSplash}
  <div class="splash-screen" class:splash-fading={splashFading}>
    <div class="splash-content">
      <img src="/logo-splash.png" alt="Provider Drop" class="splash-logo" />
      <h1 class="splash-title">
        <span class="splash-title-light">PROVIDER</span><span
          class="splash-title-bold">DROP</span
        >
      </h1>
      <div class="splash-loader">
        <div class="splash-loader-bar"></div>
      </div>
    </div>
  </div>
{/if}

<div class="landing-page" class:hidden={showSplash && !splashFading}>
  <!-- Main Content -->
  <main class="main-content">
    <!-- Hero Section - At the top -->
    <section class="hero-section">
      {#if logoUrl}
        {#if logoContainerStyle === "none"}
          <!-- No container - just the logo -->
          <img
            class="hero-logo-bare"
            src={logoUrl}
            alt={companyName}
            style="max-width: {logoMaxWidth > 0
              ? logoMaxWidth + 'px'
              : '400px'}; max-height: {logoMaxHeight > 0
              ? logoMaxHeight + 'px'
              : '100px'};"
          />
        {:else}
          <!-- Logo with container -->
          <div
            class="hero-logo-container"
            class:container-card={logoContainerStyle === "card"}
            class:container-border={logoContainerStyle === "border"}
            style="background: {logoBackground && logoBackground !== 'none'
              ? logoBackground
              : '#f8f9fa'};"
          >
            <img
              src={logoUrl}
              alt={companyName}
              style="max-width: {logoMaxWidth > 0
                ? logoMaxWidth + 'px'
                : '400px'}; max-height: {logoMaxHeight > 0
                ? logoMaxHeight + 'px'
                : '100px'};"
            />
          </div>
        {/if}
      {:else}
        <div class="hero-logo-placeholder">
          <Icon name="Upload" size={48} />
        </div>
      {/if}
      {#if !hideCompanyName}
        <h1 class="hero-title">{companyName}</h1>
      {/if}
      <p class="hero-subtitle">{appDescription}</p>
    </section>

    <!-- Notice Banners -->
    <NoticeBanners {notices} />

    <!-- Steps Card -->
    <div class="steps-card">
      <h2 class="steps-title">3 Easy Steps</h2>

      <div class="steps-connector"></div>

      <div class="steps-list">
        <div class="step-item">
          <div class="step-icon step-icon-1">
            <Icon name="Mail" size={20} />
          </div>
          <div class="step-content">
            <h3>1. Verify Email</h3>
            <p>Quick email verification for security</p>
          </div>
        </div>

        <div class="step-item">
          <div class="step-icon step-icon-2">
            <Icon name="Upload" size={20} />
          </div>
          <div class="step-content">
            <h3>2. Select Files</h3>
            <p>Choose your files with our drag & drop interface</p>
          </div>
        </div>

        <div class="step-item">
          <div class="step-icon step-icon-3">
            <Icon name="CheckCircle" size={20} />
          </div>
          <div class="step-content">
            <h3>3. Upload Securely</h3>
            <p>Your files are encrypted and safely transmitted</p>
          </div>
        </div>
      </div>

      <div class="steps-action">
        <Button size="lg" onclick={onGetStarted}>
          <Icon name="Upload" size={20} />
          Start Upload
        </Button>
      </div>
    </div>

    <!-- Feature Badges -->
    <div class="feature-badges">
      <div class="feature-badge">
        <div class="feature-badge-icon">
          <Icon name="Shield" size={18} />
        </div>
        <span>Bank-Level Security</span>
      </div>
      <div class="feature-badge">
        <div class="feature-badge-icon">
          <Icon name="Zap" size={18} />
        </div>
        <span>Fast Upload</span>
      </div>
      <div class="feature-badge">
        <div class="feature-badge-icon">
          <Icon name="CheckCircle" size={18} />
        </div>
        <span>HIPAA Compliant</span>
      </div>
    </div>
  </main>

  <!-- Footer with Contact Info -->
  <footer class="footer">
    <div class="footer-content">
      <div class="footer-brand">
        {#if logoUrl}
          <img src={logoUrl} alt={companyName} class="footer-logo" />
        {/if}
        <span class="footer-company">{companyName}</span>
      </div>

      {#if hasContactInfo}
        <div class="footer-contact">
          {#if address}
            <div class="contact-item contact-address">
              <Icon name="MapPin" size={16} />
              <span>{@html address.replace(/\n/g, "<br>")}</span>
            </div>
          {/if}

          {#if phone || email || supportUrl}
            <div class="contact-links">
              {#if phone}
                <div class="contact-item">
                  <Icon name="Phone" size={16} />
                  <a href="tel:{phone.replace(/[^0-9+]/g, '')}">{phone}</a>
                </div>
              {/if}
              {#if email}
                <div class="contact-item">
                  <Icon name="Mail" size={16} />
                  <a href="mailto:{email}">{email}</a>
                </div>
              {/if}
              {#if supportUrl}
                <div class="contact-item">
                  <Icon name="ExternalLink" size={16} />
                  <a href={supportUrl} target="_blank" rel="noopener noreferrer"
                    >Support</a
                  >
                </div>
              {/if}
            </div>
          {/if}
        </div>
      {/if}

      <!-- Report Issue Button -->
      <button
        class="footer-report-btn"
        onclick={() => (showSupportModal = true)}
      >
        <Icon name="AlertCircle" size={14} />
        <span>Report Issue</span>
      </button>
    </div>
  </footer>

  <!-- Support Modal -->
  <SupportModal
    isOpen={showSupportModal}
    onClose={() => (showSupportModal = false)}
  />

  <!-- Notice Modals -->
  <NoticeModals {notices} />
</div>

<style>
  /* Splash Screen */
  .splash-screen {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #3d5a7f 100%);
    transition:
      opacity 0.6s ease-out,
      transform 0.6s ease-out;
  }

  .splash-screen.splash-fading {
    opacity: 0;
    transform: scale(1.05);
  }

  .splash-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-8);
    animation: splash-enter 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes splash-enter {
    from {
      opacity: 0;
      transform: scale(0.8) translateY(20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  .splash-logo {
    width: 160px;
    height: 160px;
    object-fit: contain;
    filter: drop-shadow(0 8px 32px rgba(0, 0, 0, 0.3));
    animation: splash-float 2s ease-in-out infinite;
  }

  @keyframes splash-float {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  .splash-title {
    font-size: 2rem;
    letter-spacing: 0.3em;
    color: white;
    margin: 0;
    text-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
    animation: splash-title-enter 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both;
  }

  @keyframes splash-title-enter {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .splash-title-light {
    font-weight: 300;
  }

  .splash-title-bold {
    font-weight: 700;
  }

  .splash-loader {
    width: 120px;
    height: 3px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    overflow: hidden;
  }

  .splash-loader-bar {
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.8),
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0.8)
    );
    border-radius: 3px;
    animation: splash-loading 1.5s ease-in-out infinite;
    transform-origin: left;
  }

  @keyframes splash-loading {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  .landing-page.hidden {
    visibility: hidden;
  }

  .landing-page {
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    background: linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 50%, #3d5a7f 100%);
  }

  /* Main Content */
  .main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--space-6) var(--space-4);
    gap: var(--space-8);
  }

  @media (min-width: 640px) {
    .main-content {
      padding: var(--space-8);
      gap: var(--space-10);
    }
  }

  /* Steps Card */
  .steps-card {
    width: 100%;
    max-width: 480px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
    position: relative;
  }

  @media (min-width: 640px) {
    .steps-card {
      padding: var(--space-8);
    }
  }

  .steps-title {
    font-size: var(--text-xl);
    font-weight: var(--font-bold);
    color: var(--color-gray-800);
    margin: 0 0 var(--space-6);
    text-align: center;
  }

  @media (min-width: 640px) {
    .steps-title {
      font-size: var(--text-2xl);
    }
  }

  /* Steps connector line */
  .steps-connector {
    display: none;
  }

  @media (min-width: 640px) {
    .steps-connector {
      display: block;
      position: absolute;
      left: 56px;
      top: 120px;
      width: 2px;
      height: 140px;
      background: linear-gradient(
        180deg,
        var(--color-primary-200) 0%,
        var(--color-primary-300) 50%,
        var(--color-primary-200) 100%
      );
      z-index: 0;
    }
  }

  .steps-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    position: relative;
    z-index: 1;
  }

  .step-item {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
  }

  @media (min-width: 640px) {
    .step-item {
      gap: var(--space-4);
    }
  }

  .step-icon {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-full);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .step-icon-1 {
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  }

  .step-icon-2 {
    background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  }

  .step-icon-3 {
    background: linear-gradient(135deg, #10b981 0%, #3b82f6 100%);
  }

  .step-content {
    flex: 1;
    padding-top: var(--space-1);
  }

  .step-content h3 {
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-gray-800);
    margin: 0 0 var(--space-1);
  }

  .step-content p {
    font-size: var(--text-xs);
    color: var(--color-gray-500);
    margin: 0;
    line-height: 1.4;
  }

  @media (min-width: 640px) {
    .step-content h3 {
      font-size: var(--text-base);
    }

    .step-content p {
      font-size: var(--text-sm);
    }
  }

  /* Steps Action */
  .steps-action {
    margin-top: var(--space-6);
    display: flex;
    justify-content: center;
  }

  .steps-action :global(button) {
    width: 100%;
    max-width: 280px;
    justify-content: center;
    gap: var(--space-2);
  }

  /* Hero Section */
  .hero-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: var(--space-4);
  }

  /* Logo without container */
  .hero-logo-bare {
    display: block;
    width: auto;
    height: auto;
  }

  /* Logo with container */
  .hero-logo-container {
    display: inline-block;
    padding: var(--space-3);
    border-radius: var(--radius-xl);
    line-height: 0;
    /* Background color set via inline style from config */
  }

  .hero-logo-container.container-card {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  }

  .hero-logo-container.container-border {
    border: 2px solid rgba(255, 255, 255, 0.3);
  }

  .hero-logo-container img {
    display: block;
    width: auto;
    height: auto;
  }

  /* Placeholder when no logo */
  .hero-logo-placeholder {
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-xl);
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  .hero-title {
    font-size: var(--text-2xl);
    font-weight: var(--font-bold);
    color: white;
    margin: 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  .hero-subtitle {
    font-size: var(--text-base);
    color: rgba(255, 255, 255, 0.85);
    margin: 0;
    max-width: 400px;
  }

  @media (min-width: 640px) {
    /* .hero-logo {
      width: 100px;
      height: 100px;
    } */

    .hero-title {
      font-size: var(--text-3xl);
    }

    .hero-subtitle {
      font-size: var(--text-lg);
    }
  }

  /* Feature Badges - inline style for hero */
  .feature-badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--space-3);
    margin-top: var(--space-2);
  }

  .feature-badge {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    background: rgba(255, 255, 255, 0.15);
    border-radius: var(--radius-full);
    backdrop-filter: blur(4px);
  }

  .feature-badge-icon {
    width: 28px;
    height: 28px;
    border-radius: var(--radius-full);
    background: rgba(255, 255, 255, 0.2);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .feature-badge span {
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: white;
  }

  /* Footer */
  .footer {
    flex-shrink: 0;
    padding: var(--space-3) var(--space-4);
    background: rgba(15, 30, 50, 0.98);
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .footer-content {
    max-width: 900px;
    margin: 0 auto;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: center;
    gap: var(--space-2) var(--space-4);
  }

  .footer-brand {
    display: flex;
    align-items: flex-start;
    gap: var(--space-2);
  }

  .footer-logo {
    width: 24px;
    height: 24px;
    border-radius: var(--radius-sm);
    object-fit: contain;
    background: white;
    padding: 2px;
  }

  .footer-company {
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: white;
  }

  .footer-contact {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .contact-links {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-1);
  }

  .contact-item {
    display: flex;
    align-items: flex-start;
    gap: var(--space-1);
    color: rgba(255, 255, 255, 0.9);
    font-size: var(--text-xs);
  }

  .contact-item.contact-address :global(svg) {
    margin-top: 2px;
  }

  .contact-item a {
    color: rgba(255, 255, 255, 0.9);
    text-decoration: none;
    transition: color 0.2s;
  }

  .contact-item a:hover {
    color: white;
    text-decoration: underline;
  }

  .contact-item :global(svg) {
    opacity: 0.7;
    flex-shrink: 0;
  }

  @media (min-width: 640px) {
    .footer {
      padding: var(--space-3) var(--space-6);
    }

    .footer-content {
      flex-direction: row;
      justify-content: space-between;
      flex-wrap: nowrap;
      gap: var(--space-4);
    }

    .footer-logo {
      width: 32px;
      height: 32px;
    }

    .footer-company {
      font-size: var(--text-base);
    }

    .footer-contact {
      align-items: flex-start;
      gap: var(--space-6);
    }

    .contact-links {
      align-items: flex-start;
    }
    .contact-item {
      font-size: var(--text-sm);
      gap: var(--space-2);
    }
  }

  /* Footer Report Issue Button */
  .footer-report-btn {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    padding: var(--space-1) var(--space-3);
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: var(--radius-full);
    cursor: pointer;
    font-size: var(--text-xs);
    font-weight: 500;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .footer-report-btn:hover {
    background: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.9);
    border-color: rgba(255, 255, 255, 0.3);
  }

  .footer-report-btn:active {
    transform: scale(0.98);
  }
</style>
